/**
 * Address Input Component
 * Smart address search field with autocomplete.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { searchAddress, type GeocodingResult } from '../services/geocodingService';
import { debounce } from '../utils/performance';
import type { Coordinates } from '../types';
import { useTranslation } from '../hooks/useTranslation.tsx';
import { useAuth } from '../hooks';
import { trackAddressSearch, trackAddressSelection } from '../services/analyticsService';
import styles from './AddressInput.module.css';

/**
 * Props for Address Input Component
 */
export interface AddressInputProps {
  /** Current origin coordinates */
  origin: Coordinates | null;
  /** Callback when address is selected */
  onAddressSelect: (coords: Coordinates) => void;
  /** Callback when input changes */
  onInputChange?: (value: string) => void;
}

/**
 * Address Input Component
 * Provides autocomplete address search.
 */
export const AddressInput: React.FC<AddressInputProps> = ({
  origin,
  onAddressSelect,
  onInputChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Debounced search function
  const performSearch = useCallback(
    async (query: string) => {
      if (!query || query.trim().length < 2) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchAddress(query, {
          limit: 5,
          proximity: origin || undefined,
        });
        setSuggestions(results);
        setShowSuggestions(true);
        setSelectedIndex(-1);
        
        // Track search after results are fetched (only for authenticated users)
        if (user?.id && query.trim().length >= 3) {
          trackAddressSearch(user.id, query.trim());
        }
        
        // Track in PostHog
        const { trackEvent } = await import('../services/posthogService');
        trackEvent('address_searched', {
          query: query.trim(),
          results_count: results.length,
        });
      } catch (error) {
        console.error('Address search error:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [origin, user]
  );

  // Create debounced version
  const debouncedSearchRef = useRef(
    debounce((query: string) => {
      void performSearch(query);
    }, 300)
  );

  useEffect(() => {
    debouncedSearchRef.current = debounce((query: string) => {
      void performSearch(query);
    }, 300);
  }, [performSearch]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (onInputChange) {
      onInputChange(value);
    }
    debouncedSearchRef.current(value);
  };

  // Handle address selection
  const handleSelect = useCallback(
    (result: GeocodingResult) => {
      setInputValue(result.address || result.placeName);
      setShowSuggestions(false);
      setSuggestions([]);
      onAddressSelect(result.coordinates);
      
      // Track address selection for analytics (if user is authenticated)
      if (user?.id) {
        trackAddressSelection(
          user.id,
          result.address || result.placeName,
          result.coordinates
        );
      }
      
      // Track in PostHog
      (async () => {
        const { trackEvent } = await import('../services/posthogService');
        trackEvent('address_selected', {
          address: result.address || result.placeName,
          place_name: result.placeName,
          coordinates: result.coordinates,
        });
      })();
    },
    [onAddressSelect, user]
  );

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        } else if (suggestions.length > 0) {
          handleSelect(suggestions[0]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.addressInputContainer} data-intro-step="1">
      <div className={styles.inputWrapper}>
        <input
          id="address-input"
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={t.addressInput.placeholder}
          className={styles.input}
          autoComplete="off"
          aria-label={t.addressInput.label}
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-controls="address-suggestions"
        />
        {isLoading && (
          <div className={styles.loading} aria-label={t.addressInput.loading}>
            <span>...</span>
          </div>
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div
          id="address-suggestions"
          ref={suggestionsRef}
          className={styles.suggestions}
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.coordinates.lat}-${suggestion.coordinates.lng}-${index}`}
              className={`${styles.suggestion} ${
                index === selectedIndex ? styles.selected : ''
              }`}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <div className={styles.suggestionText}>
                <strong>{suggestion.placeName}</strong>
              </div>
              {suggestion.address && (
                <div className={styles.suggestionAddress}>{suggestion.address}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {showSuggestions && suggestions.length === 0 && inputValue.length >= 2 && !isLoading && (
        <div className={styles.noResults}>{t.addressInput.noResults}</div>
      )}
    </div>
  );
};

